/**
 * EditMilestoneModal — edit a milestone's TEMPLATE and SUBTITLE.
 *
 * Opens via CustomEvent('open-edit-ms-modal', { detail: { msId } }) from the milestone ⋯ menu.
 *   - Template = the `Deliverable` link (multipleRecordLinks → Project Deliverable Types, tblfulsOyOyjOGXab).
 *   - Subtitle = the `Subtitle` single-line text.
 * On save it PATCHes the milestone; Airtable returns the record with recomputed formula fields
 * (Full Deliverable Name, Name (from Deliverable), …), which we splice back into ctx.state.milestones
 * so the row label updates immediately.
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { getMilestoneTemplates, updateMilestoneMeta } from '../services/milestones.js'

const getFirst = (v) => Array.isArray(v) ? v[0] : v

const labelStyle = { display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase' }
const inputStyle = { width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, boxSizing: 'border-box', outline: 'none' }

export default function EditMilestoneModal() {
  const [open, setOpen]         = useState(false)
  const [msId, setMsId]         = useState(null)
  const [templateId, setTemplateId] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [templates, setTemplates] = useState(null)   // null = loading; [{id,name}]
  const [saving, setSaving]     = useState(false)
  const [error, setError]       = useState(null)

  useEffect(() => {
    function onOpen(e) {
      const id = e.detail && e.detail.msId
      if (!id) return
      const ms = (ctx.state?.milestones || []).find(m => m.id === id)
      const f = (ms && ms.fields) || {}
      setMsId(id)
      setTemplateId(getFirst(f['Deliverable']) || '')
      setSubtitle(f['Subtitle'] || '')
      setError(null); setSaving(false); setOpen(true)
      if (templates === null) {
        getMilestoneTemplates()
          .then(recs => setTemplates((recs || []).map(r => ({ id: r.id, name: r.fields['Name'] || r.id }))))
          .catch(() => setTemplates([]))
      }
    }
    window.addEventListener('open-edit-ms-modal', onOpen)
    return () => window.removeEventListener('open-edit-ms-modal', onOpen)
  }, [templates])

  useEffect(() => {
    if (!open) return
    document.body.style.overflow = 'hidden'
    function onKey(e) { if (e.key === 'Escape' && !saving) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', onKey) }
  }, [open, saving])

  async function handleSave() {
    if (!msId || !templateId) { setError('Pick a milestone template.'); return }
    setSaving(true); setError(null)
    try {
      const rec = await updateMilestoneMeta(msId, { templateId, subtitle })
      // Splice the recomputed record back into the loaded milestone set so the row updates now.
      const arr = ctx.state?.milestones || []
      const idx = arr.findIndex(m => m.id === msId)
      if (idx >= 0 && rec && rec.fields) arr[idx] = { ...arr[idx], fields: { ...arr[idx].fields, ...rec.fields } }
      window.dispatchEvent(new Event('outline-render'))
      setOpen(false)
    } catch (ex) { setError(ex.message || 'Failed to save'); setSaving(false) }
  }

  if (!open) return null

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="EditMilestoneModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 460, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>Edit Milestone</span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>Milestone template</label>
            {templates === null ? (
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)' }}>Loading templates…</div>
            ) : (
              <select style={inputStyle} value={templateId} onChange={e => setTemplateId(e.target.value)}>
                <option value="">Select a template…</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            )}
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: 'var(--aj-text-muted)', marginTop: 4 }}>
              Changing the template updates the milestone's deliverable type. Existing checklist items and assignments are not rebuilt.
            </div>
          </div>

          <div>
            <label style={labelStyle}>Subtitle <span style={{ color: 'var(--aj-text-muted)' }}>(optional)</span></label>
            <input style={inputStyle} value={subtitle} placeholder="e.g. PPC 1st Submission"
              onChange={e => setSubtitle(e.target.value)} />
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: saving ? 'var(--aj-bg-muted)' : '#07272D', color: saving ? 'var(--aj-text-muted)' : '#fff', border: 'none', borderRadius: 4, cursor: saving ? 'default' : 'pointer' }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
